import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

const LoginPage = ({ setUserRole }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError(null);

    try {
      const decodedToken = jwtDecode(credentialResponse.credential);
      const email = decodedToken.email;
      const firstName = decodedToken.given_name; // Extract first name
      const lastName = decodedToken.family_name; // Extract last name

      // Log the API URL for debugging
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/system-users/sso-login`;

      let response;
      let retryCount = 0;
      const maxRetries = 2;

      // Retry logic for network failures
      while (retryCount <= maxRetries) {
        try {
          response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_API_KEY,
            },
            body: JSON.stringify({ email }),
          });
          break; // Success, exit retry loop
        } catch (fetchError) {
          retryCount++;
          console.error(`Network request failed (attempt ${retryCount}/${maxRetries + 1}):`, fetchError);
          
          if (retryCount > maxRetries) {
            // Provide more specific error messages based on the error type
            let errorMessage = "Unable to connect to the server. ";
            
            if (fetchError.message.includes("Failed to fetch")) {
              errorMessage += "Please check your internet connection or try again later.";
            } else if (fetchError.message.includes("ERR_FAILED")) {
              errorMessage += "The server may be temporarily unavailable. Please try again in a few moments.";
            } else if (fetchError.message.includes("CORS")) {
              errorMessage += "There's a configuration issue. Please contact support.";
            } else {
              errorMessage += fetchError.message;
            }
            
            // Log environment variables for debugging (without exposing sensitive values)
            console.error("Debug Info:");
            console.error("- API URL configured:", !!process.env.REACT_APP_API_URL);
            console.error("- API URL value:", process.env.REACT_APP_API_URL || "NOT SET");
            console.error("- Full URL attempted:", apiUrl);
            
            throw new Error(errorMessage);
          }
          
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount - 1)));
        }
      }

      if (!response.ok) {
        // Attempt to parse error message from backend response
        let errorMessage = `Login failed (Status: ${response.status}). Please check if your email is registered or contact support.`;
        try {
          const errorResult = await response.json();
          if (errorResult && errorResult.message) {
            errorMessage = errorResult.message; // Use backend error message if available
          }
        } catch (parseError) {
          // Log parsing error but proceed with the generic status message
          console.error("Failed to parse error response body:", parseError);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        const { role, user_id, barangay_code, municipal_code, provincial_code } = result.data; // Email is already available from decodedToken
        localStorage.setItem("barangayCode", barangay_code);
        localStorage.setItem("municipalCode", municipal_code);
        localStorage.setItem("provincialCode", provincial_code)
        localStorage.setItem("userRole", role);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userId", user_id);
        localStorage.setItem("userFirstName", firstName); // Store first name
        localStorage.setItem("userLastName", lastName); // Store last name

        // Update parent state
        setUserRole(role);

        // Navigate to MainFormContainer with state
        navigate("/main", {
          replace: true,
        });
      } else {
        // Handle cases where the API call was successful (2xx status) but login failed logically
        setError(result.message || "Login failed. Please check your credentials or contact support.");
      }
    } catch (error) {
      // Catch network errors or errors thrown from the !response.ok block
      console.error("Login error:", error); // Log the actual error for debugging
      setError(error.message || "An unexpected error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_AUTH_KEY}>
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundImage: "url('/BIDANI_LOGIN.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "contain",
          minHeight: "100vh",
          minWidth: "100vw",
          height: "100vh",
          width: "100vw",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "rgba(200, 0, 0, 0.85)",
            borderRadius: "12px",
            padding: "32px 24px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            maxWidth: 360,
          }}
        >
          {isLoading ? (
            <p style={{ color: "#fff" }}>Authenticating...</p>
          ) : (
            <>
              {error && (
                <div style={{ color: "#fff", background: "#b71c1c", marginBottom: "20px", padding: "10px", border: "1px solid #fff", borderRadius: "4px" }}>
                  {error}
                </div>
              )}
              <GoogleLogin
                onSuccess={handleSuccess}
                onError={() => setError("Google authentication failed")}
                useOneTap
                theme="outline"
                size="large"
                text="signin_with"
              />
            </>
          )}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
